import { useEffect, useState } from "react";
import { api } from "@/hub/lib/api";
import { MapPin, Tent, Home, Search, Check, XCircle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const ZONE_IMG = {
  outdoor: "https://images.unsplash.com/photo-1635314924786-f3a501a87458?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHwzfHxnbGFtcGluZyUyMHJlc29ydHxlbnwwfHx8fDE3ODgxMjE3MDZ8MA&ixlib=rb-4.1.0&q=85",
  accommodation: "https://images.unsplash.com/photo-1721886473357-1563de6e6883?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHwyfHxnbGFtcGluZyUyMHJlc29ydHxlbnwwfHx8fDE3ODgxMjE3MDZ8MA&ixlib=rb-4.1.0&q=85",
};

export default function Zones() {
  const [zones, setZones] = useState([]);
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [availability, setAvailability] = useState([]);

  useEffect(() => { api.zones().then(setZones); }, []);
  useEffect(() => { if (date) api.availability(date).then(setAvailability); }, [date]);

  const availMap = {};
  availability.forEach((a) => { availMap[a.zone.id] = a; });

  const outdoor = zones.filter((z) => z.type === "outdoor");
  const accom = zones.filter((z) => z.type === "accommodation");

  const totalOutdoor = outdoor.reduce((s, z) => s + z.capacity, 0);
  const totalAccom = accom.reduce((s, z) => s + z.capacity, 0);

  return (
    <div className="space-y-6">
      <div className="bg-white border border-border p-5 flex flex-wrap items-center gap-4 justify-between">
        <div className="flex gap-8">
          <div>
            <div className="text-xs uppercase tracking-[0.15em] text-stone-400">Total Outdoor Capacity</div>
            <div className="font-mono text-3xl font-light text-forest">{totalOutdoor}</div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.15em] text-stone-400">Total Accommodation</div>
            <div className="font-mono text-3xl font-light text-terracotta">{totalAccom}</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Search className="h-4 w-4 text-stone-400" />
          <span className="text-sm text-stone-500">Check availability on</span>
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="rounded-sm w-40" data-testid="availability-date" />
        </div>
      </div>

      <div>
        <h3 className="font-heading text-lg font-medium mb-3 flex items-center gap-2"><MapPin className="h-4 w-4 text-forest" /> Outdoor Zones</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {outdoor.map((z) => <ZoneCard key={z.id} zone={z} avail={availMap[z.id]} img={ZONE_IMG.outdoor} icon={MapPin} />)}
        </div>
      </div>

      <div>
        <h3 className="font-heading text-lg font-medium mb-3 flex items-center gap-2"><Tent className="h-4 w-4 text-terracotta" /> Accommodation</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {accom.map((z) => <ZoneCard key={z.id} zone={z} avail={availMap[z.id]} img={ZONE_IMG.accommodation} icon={z.name.includes("Tent") ? Tent : Home} />)}
        </div>
      </div>
    </div>
  );
}

function ZoneCard({ zone, avail, img, icon: Icon }) {
  const available = avail?.available;
  return (
    <div className="bg-white border border-border overflow-hidden group" data-testid={`zone-card-${zone.id}`}>
      <div className="h-28 relative overflow-hidden">
        <img src={img} alt={zone.name} className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" />
        <div className="absolute inset-0" style={{ background: `linear-gradient(to top, ${zone.color}dd, transparent)` }} />
        <div className="absolute bottom-2 left-3 right-3 flex items-end justify-between">
          <div className="flex items-center gap-2 text-white">
            <Icon className="h-4 w-4" />
            <span className="font-heading font-medium">{zone.name}</span>
          </div>
          <span className="font-mono text-white text-lg font-light">{zone.capacity}</span>
        </div>
      </div>
      <div className="p-3 flex items-center justify-between">
        <span className="text-xs text-stone-400">{zone.description}</span>
        {avail && (
          available ? (
            <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-600" data-testid={`zone-avail-${zone.id}`}><Check className="h-3.5 w-3.5" /> Available</span>
          ) : (
            <span className="inline-flex items-center gap-1 text-xs font-semibold text-red-500" title={`Reserved by ${avail.reserved_by}`} data-testid={`zone-avail-${zone.id}`}><XCircle className="h-3.5 w-3.5" /> {avail.reserved_by}</span>
          )
        )}
      </div>
    </div>
  );
}
